import './statistics.css'

import { useEffect, useState } from 'react'

import { Spinner } from '../../Components'
import { useAuth } from '../../Provider/AuthProvider'
import { useCQuery } from '../../Services'
import { cap } from '../../State'
import { useCapsule } from '../../State/Functions'
import {
  StatisticsDateSection,
  StatisticsDetailGrid,
  StatisticsFiltersSection,
  StatisticsHeader,
  StatisticsKpiGrid
} from './Components'
import { COSTO_PER_CONVERSAZIONE } from './constants'
import { getPercentage, labelBarChart } from './statisticsFunctions'

/**
 * Pagina principale delle statistiche con layout dashboard.
 * Visualizza KPI di riepilogo, percentuali elaborate e dettagli errori.
 * @returns {JSX.Element} Componente pagina statistiche
 */
export const Statistics = () => {
  const [startDate, setStartDate] = useCapsule(cap.selectedStartDateDashboard)
  const [endDate, setEndDate] = useCapsule(cap.selectedEndDateDashboard)
  const [spinnerOperation, setSpinnerOperation] = useState(false)

  const [filters, setFilters] = useState({
    isola: '',
    unitOp: '',
    codCliente: '',
    status: ''
  })

  const { data: isoleList = [], isSuccess: isSuccessIsole } = useCQuery(['isoleConversations'])
  const { data: unitOpList = [], isSuccess: isSuccessUnitOp } = useCQuery(['unitOpConversations'])
  // const { data: codClientiList = [], isSuccess: isSuccessCodClienti } = useCQuery(['codClientiConversations'])

  // Query principale con filtri
  const { data, isSuccess } = useCQuery([
    'getDashboardStatsConversations',
    startDate,
    endDate,
    filters
  ])

  const [percentageStateTotal, setPercentageStateTotal] = useState([])
  const [handledPercentage, setHandledPercentage] = useState({})
  const { user: { user = {} } = {} } = useAuth()
  const [totalPriceGeneral, setTotalPriceGeneral] = useState({ counter: 0, total: 0 })
  const [totalPriceFatturato, setTotalPriceFatturato] = useState({ counter: 0, total: 0 })

  /**
   * Carica e processa i dati delle conversazioni per le statistiche
   * @param {Object} dataToLoad - Dati delle conversazioni dal server
   */
  const loadDataConversation = (dataToLoad) => {
    const {
      conversations = [],
      trasfertaConversations = [],
      realError = [],
      noraIsExcluded,
      errorHandled = [],
      openPauseConversation = [],
      ignoredConversation = [],
      handledByOperator = []
    } = dataToLoad

    const totalConversationsLength = conversations.length - errorHandled.length

    const handledUniqueIds = new Set([
      ...trasfertaConversations.map((c) => c.id),
      ...openPauseConversation.map((c) => c.id),
      ...handledByOperator.map((c) => c.id),
      ...ignoredConversation.map((c) => c.id)
    ])
    const handledUnique = [...handledUniqueIds]

    const percentages = {
      errorHandled: getPercentage(conversations.length, errorHandled) || 0,
      trasfertaConversations: getPercentage(totalConversationsLength, trasfertaConversations) || 0,
      openPauseConversation: getPercentage(totalConversationsLength, openPauseConversation) || 0,
      ignoredConversation: getPercentage(totalConversationsLength, ignoredConversation) || 0,
      handledByOperator: getPercentage(totalConversationsLength, handledByOperator) || 0,
      realError: getPercentage(totalConversationsLength, realError) || 0,
      noraIsExcluded: getPercentage(totalConversationsLength, noraIsExcluded) || 0,
      elaborated: getPercentage(conversations.length, handledUnique) || 0,
      errorTotal: getPercentage(conversations.length, [...realError, ...noraIsExcluded]) || 0
    }

    const percentageBarChart = Object.keys(percentages).map((key) => ({
      name: key,
      label: labelBarChart(key),
      value: percentages[key]
    }))
    setPercentageStateTotal(percentageBarChart)

    const counter = handledUniqueIds.size
    setHandledPercentage({
      perc: parseFloat(((counter / totalConversationsLength) * 100 || 0).toFixed(2)),
      totalHandled: counter,
      totalError: realError.length + noraIsExcluded.length,
      totalConversationsLength
    })

    if (user?.role === 'fatturazione') {
      const allHandled = [
        ...trasfertaConversations,
        ...openPauseConversation,
        ...handledByOperator,
        ...ignoredConversation
      ]
      const seenBilled = new Set()
      const seenNotBilled = new Set()
      allHandled.forEach((item) => {
        if (item?.billed && !seenBilled.has(item.id)) seenBilled.add(item.id)
        else if (!item?.billed && !seenNotBilled.has(item.id)) seenNotBilled.add(item.id)
      })
      setTotalPriceFatturato({
        total: seenBilled.size * COSTO_PER_CONVERSAZIONE,
        counter: seenBilled.size
      })
      setTotalPriceGeneral({
        total: seenNotBilled.size * COSTO_PER_CONVERSAZIONE,
        counter: seenNotBilled.size
      })
    } else {
      setTotalPriceGeneral({
        total: counter * COSTO_PER_CONVERSAZIONE,
        counter
      })
    }
  }

  useEffect(() => {
    if (isSuccess && data) {
      loadDataConversation(data)
    }
  }, [data, isSuccess])

  const filtersLoaded = isSuccessIsole && isSuccessUnitOp

  if (!isSuccess || !filtersLoaded) {
    return <Spinner />
  }

  const isFatturazione = user?.role === 'fatturazione'
  const isAdmin = user?.role === 'admin'

  return (
    <div className="stats-container">
      <StatisticsHeader
        isAdmin={isAdmin}
        isFatturazione={isFatturazione}
        spinnerOperation={spinnerOperation}
        startDate={startDate}
        endDate={endDate}
        data={data}
        totalPriceGeneral={totalPriceGeneral}
        setSpinnerOperation={setSpinnerOperation}
      />

      <StatisticsDateSection
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />

      {!isFatturazione && (
        <StatisticsFiltersSection
          filters={filters}
          setFilters={setFilters}
          isoleList={isoleList}
          unitOpList={unitOpList}
          // codClientiList={codClientiList}
        />
      )}

      <StatisticsKpiGrid
        isFatturazione={isFatturazione}
        totalPriceGeneral={totalPriceGeneral}
        totalPriceFatturato={totalPriceFatturato}
        handledPercentage={handledPercentage}
        data={data}
      />

      {!isFatturazione && (
        <StatisticsDetailGrid
          data={data}
          percentageStateTotal={percentageStateTotal}
          handledPercentage={handledPercentage}
        />
      )}
    </div>
  )
}
